/**
 * flowEngine — shared rendering engine for the monitor flow diagrams (Message pipeline + Event
 * ingest pipeline). Owns the responsive scaling, the animated TPS edges, the Redis liquid-jars,
 * the service CPU/RAM/Disk cards, and the orthogonal edge routers. Each diagram supplies its own
 * `layout` (node boxes) and `buildEdges(L, flow)` (which composes the routers from makeRouters).
 */
import React, { useMemo, useRef, useState, useEffect } from "react";
import { Box, Flex, HStack, VStack, Text, Badge,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
  Table, Thead, Tbody, Tr, Th, Td, Divider } from "@chakra-ui/react";
import { TONE, fmtInt } from "./helpers";

export const FLOW = "#6d7cff";
export const BUF_HW = 80, BUF_HH = 55; // half-extents of the (wide) buffer card, so edges land on its edges
export const fmtTps = (n) => (n == null ? "—" : `${Number(n).toLocaleString("en-US", { maximumFractionDigits: 1 })}/s`);
export const dur = (tps) => (!tps || tps <= 0 ? 6.5 : Math.max(1, Math.min(4.5, 34 / tps)));
export const loadTone = (p) => (p == null ? "muted" : p >= 90 ? "crit" : p >= 70 ? "warn" : "ok");
export const cpuT = (v) => (v == null ? "muted" : v >= 80 ? "crit" : v >= 60 ? "warn" : "ok");
export const ramT = (v) => (v == null ? "muted" : v >= 90 ? "crit" : v >= 75 ? "warn" : "ok");

// Geometry — all take the (scaled) layout L so the engine is layout-agnostic.
export const cxf = (L, n) => L[n].x + L[n].w / 2, cyf = (L, n) => L[n].y + L[n].h / 2;
export const isBuf = (L, n) => L[n].kind === "buffer";
export const rightMid = (L, n) => [isBuf(L, n) ? cxf(L, n) + BUF_HW : L[n].x + L[n].w, cyf(L, n)];
export const leftMid = (L, n) => [isBuf(L, n) ? cxf(L, n) - BUF_HW : L[n].x, cyf(L, n)];
export const botMid = (L, n) => [cxf(L, n), isBuf(L, n) ? cyf(L, n) + BUF_HH : L[n].y + L[n].h];
export const topMid = (L, n) => [cxf(L, n), isBuf(L, n) ? cyf(L, n) - BUF_HH : L[n].y];

// Straight/orthogonal edge routers, bound to a layout + flow lookup. All lines are axis-aligned.
export function makeRouters(L, flow) {
  const seg = (...p) => "M" + p.map((q) => `${q[0]},${q[1]}`).join(" L");
  const mk = (a, b, d, lx, ly, opts = {}) => ({ id: `${a}__${b}`, f: flow(a, b), d, lx, ly, lpos: opts.lpos || "up", ret: opts.ret, dashed: opts.dashed });
  const gapMid = (a, b) => (L[a].x + L[a].w + L[b].x) / 2;
  // Horizontal (same row): box-right → box-left, label centered in the gap above the line.
  const Hz = (a, b, o = {}) => { const P = rightMid(L, a), Q = leftMid(L, b); return mk(a, b, seg(P, Q), gapMid(a, b), P[1] - 20, o); };
  // Vertical/orthogonal branch down (straight if x aligns, else one jog).
  const Vd = (a, b) => { const P = botMid(L, a), Q = topMid(L, b), my = (P[1] + Q[1]) / 2; const d = Math.abs(P[0] - Q[0]) < 6 ? seg(P, Q) : seg(P, [P[0], my], [Q[0], my], Q); return mk(a, b, d, P[0] + 12, my, { lpos: "right" }); };
  // Forward orthogonal (right → vertical → into left edge). Label sits just BEFORE the target box.
  const Or = (a, b) => { const P = rightMid(L, a), Q = leftMid(L, b), mx = (P[0] + Q[0]) / 2; return mk(a, b, seg(P, [mx, P[1]], [mx, Q[1]], Q), Q[0] - 40, Q[1] - 14); };
  // Dashed lookup exits RIGHT, rides a vertical rail (OFFSET left of the target → scale-correct), enters left.
  const LkR = (a, b, off) => { const P = rightMid(L, a), Q = leftMid(L, b), railX = Q[0] - off; return mk(a, b, seg(P, [railX, P[1]], [railX, Q[1]], Q), (railX + Q[0]) / 2, Math.min(P[1], Q[1]) - 6, { dashed: true }); };
  // Dashed lookup UP into a node above: exit top, ride a horizontal rail, into target's bottom.
  const LkU = (a, b, rail) => { const P = topMid(L, a), Q = botMid(L, b); return mk(a, b, seg(P, [P[0], rail], [Q[0], rail], Q), (P[0] + Q[0]) / 2, rail - 6, { dashed: true }); };
  // Dashed call that drops BELOW everything and runs along a bottom rail.
  const RailBot = (a, b, rail) => { const P = botMid(L, a), Q = botMid(L, b); return mk(a, b, seg(P, [P[0], rail], [Q[0], rail], Q), (P[0] + Q[0]) / 2, rail + 4, { dashed: true, lpos: "below" }); };
  // Green-dashed return that rides the right margin up (OFFSET right of the source → scale-correct).
  const RetUp = (a, b, off) => { const P = rightMid(L, a), Q = rightMid(L, b), railX = P[0] + off; return mk(a, b, seg(P, [railX, P[1]], [railX, Q[1]], Q), (railX + Q[0]) / 2, Q[1] - 18, { ret: true }); };
  // Fork that drops from a top-row node, jogs along a rail below a mid-row box, into a lower node.
  const ForkDown = (a, b, rail) => { const P = botMid(L, a), Q = topMid(L, b); return mk(a, b, seg(P, [P[0], rail], [Q[0], rail], Q), Q[0] + 12, (rail + Q[1]) / 2, { lpos: "right" }); };
  return { seg, mk, rightMid: (n) => rightMid(L, n), leftMid: (n) => leftMid(L, n), botMid: (n) => botMid(L, n), topMid: (n) => topMid(L, n), Hz, Vd, Or, LkR, LkU, RailBot, RetUp, ForkDown };
}

export function toneOf(s) { return TONE[s === "crit" ? "crit" : s === "warn" ? "warn" : s === "muted" || s === "not_wired" ? "muted" : "ok"]; }
// "Bottleneck" is reserved for the ONE flow-constraining stage the verdict banner names.
const pillLabel = (s, isBottleneck) => (isBottleneck ? "Bottleneck" : { ok: "OK", warn: "Warn", crit: "Critical", not_wired: "Not wired", muted: "—" }[s] || s);

// Redis buffer drawn as a liquid vessel: silhouette + clipped fill whose LEVEL is RAM used%.
function BufferJar({ node, crit, onOpenDetail }) {
  const clickable = (node.queues || []).length > 0 && typeof onOpenDetail === "function";
  const memKnown = node.mem_pct != null;
  const pct = memKnown ? Math.max(0, Math.min(100, node.mem_pct)) : 0;
  const mt = TONE[loadTone(node.mem_pct)];
  const cxc = 30, rx = 26, ry = 6.5, topCy = 8, botCy = 44;
  const body = `M${cxc - rx},${topCy} L${cxc - rx},${botCy} A${rx},${ry} 0 0 0 ${cxc + rx},${botCy} L${cxc + rx},${topCy} A${rx},${ry} 0 0 1 ${cxc - rx},${topCy} Z`;
  const liquidY = topCy + (botCy - topCy) * (1 - pct / 100);
  const clipId = `jar-${node.id}`;
  const line = { fontFamily: "mono", lineHeight: "1.3", whiteSpace: "nowrap", maxW: "100%", overflow: "hidden", textOverflow: "ellipsis" };
  return (
    <Flex direction="row" align="center" h="100%" w="100%" gap="9px" px="10px" py="6px" overflow="hidden" bg="white" borderRadius="lg" borderWidth="1px"
      borderColor={crit ? "red.200" : "gray.150"} boxShadow={crit ? `0 0 0 2px ${mt.dot}33` : "0 1px 3px rgba(16,24,40,.05)"}
      cursor={clickable ? "pointer" : "default"} onClick={clickable ? () => onOpenDetail(node) : undefined}
      _hover={clickable ? { borderColor: "blue.300", boxShadow: "0 0 0 2px rgba(109,124,255,.18)" } : undefined}
      title={clickable ? "Click for the BullMQ queues on this Redis" : undefined} transition="border-color .15s, box-shadow .15s">
      {/* icon — jar on the left, with RAM% under it */}
      <Flex direction="column" align="center" flexShrink={0}>
        <Box as="svg" width="48px" height="42px" viewBox="0 0 60 52" style={{ display: "block", overflow: "visible" }}>
          <defs><clipPath id={clipId}><path d={body} /></clipPath></defs>
          <path d={body} fill="#f6f7f9" />
          {memKnown && pct > 0 && (
            <g clipPath={`url(#${clipId})`}>
              <rect x={cxc - rx} y={liquidY} width={rx * 2} height={botCy + ry - liquidY} fill={mt.dot} opacity="0.5" />
              <ellipse cx={cxc} cy={liquidY} rx={rx} ry={ry} fill={mt.dot} opacity="0.8" />
            </g>
          )}
          <path d={body} fill="none" stroke={mt.dot} strokeWidth="1.6" />
          <ellipse cx={cxc} cy={topCy} rx={rx} ry={ry} fill={pct >= 98 ? mt.dot : "white"} fillOpacity={pct >= 98 ? 0.8 : 1} stroke={mt.dot} strokeWidth="1.6" />
        </Box>
        <Text fontSize="13px" fontFamily="mono" fontWeight="700" color={mt.fg} lineHeight="1.1" mt="1px">{memKnown ? `${node.mem_pct}%` : "—"}</Text>
      </Flex>
      {/* info — right side */}
      <Box flex="1" minW="0">
        <Text fontSize="12.5px" fontWeight="700" color="#111827" lineHeight="1.15" {...line}>{node.title}</Text>
        {node.sub && <Text fontSize="8px" color="gray.400" letterSpacing="0.02em" {...line} fontFamily="inherit">{node.sub}</Text>}
        {node.mem_used_mb != null && (
          <Text fontSize="11px" color="gray.700" mt="2px" {...line}>{fmtMB(node.mem_used_mb)}<Text as="span" fontSize="8.5px" color="gray.400"> used</Text>{node.mem_free_mb != null ? <Text as="span" color="gray.500"> · {fmtMB(node.mem_free_mb)}<Text as="span" fontSize="8.5px" color="gray.400"> free</Text></Text> : null}</Text>
        )}
        {(node.reads_per_sec != null || node.writes_per_sec != null) && (
          <Text fontSize="11px" color="gray.600" {...line}>r {node.reads_per_sec ?? "—"} · w {node.writes_per_sec ?? "—"}<Text as="span" fontSize="8.5px" color="gray.400">/s</Text></Text>
        )}
        {node.depth != null && <Text fontSize="10px" color={node.depth > 0 ? "gray.700" : "gray.400"} {...line}>{fmtInt(node.depth)} queued</Text>}
        {clickable && <Text fontSize="8.5px" fontWeight="600" color="blue.500" mt="1px" {...line}>{node.queues.length} queues ›</Text>}
      </Box>
    </Flex>
  );
}

// Compact MB/GB formatter for datastore memory figures.
function fmtMB(mb) {
  if (mb == null) return "—";
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)}GB` : `${Math.round(mb)}MB`;
}

// One labelled metric cell for the datastore (store) node — value over an uppercase label,
// separated by a hairline divider so the row reads as distinct figures.
function StoreMetric({ label, value, accent }) {
  return (
    <Box flex="1" minW="0" textAlign="center" px="2px" borderLeftWidth="1px" borderColor="gray.100" _first={{ borderLeftWidth: 0 }}>
      <Text fontSize="14px" fontFamily="mono" fontWeight="700" color={accent || "#111827"} lineHeight="1.05" noOfLines={1}>{value}</Text>
      <Text fontSize="8px" color="gray.400" textTransform="uppercase" letterSpacing="0.05em" lineHeight="1.2" mt="1px" noOfLines={1}>{label}</Text>
    </Box>
  );
}

function MiniBar({ label, v, tone }) {
  const tn = TONE[tone(v)];
  return (
    <Flex align="center" gap="6px" w="100%">
      <Text w="26px" fontSize="8.5px" color="gray.500" flexShrink={0}>{label}</Text>
      <Box flex="1" h="5px" bg="gray.100" borderRadius="full" overflow="hidden"><Box h="100%" w={`${Math.min(100, v)}%`} bg={tn.dot} transition="width .5s ease" /></Box>
      <Text w="30px" fontSize="9px" fontFamily="mono" fontWeight="600" textAlign="right" color={tn.fg} flexShrink={0}>{v}%</Text>
    </Flex>
  );
}

function NodeCard({ node, bottleneckId, onOpenDetail }) {
  const crit = node.status === "crit", t = toneOf(node.status);
  if (node.kind === "buffer") return <BufferJar node={node} crit={crit} onOpenDetail={onOpenDetail} />;
  if (node.kind === "external") {
    return <Flex direction="column" align="center" justify="center" h="100%" borderRadius="lg" borderWidth="1.5px" borderStyle="dashed" borderColor="gray.300" bg="gray.50">
      <Text fontSize="13px" fontWeight="700" color="gray.600">{node.title}</Text><Text fontSize="8px" color="gray.400" letterSpacing="0.06em" textTransform="uppercase">{node.sub}</Text></Flex>;
  }
  if (node.kind === "store") {
    const hasStats = node.connections != null || node.connections_active != null || node.resident_mb != null || node.reads_per_sec != null;
    return <Flex direction="column" h="100%" px="11px" py="8px" borderRadius="lg" borderWidth="1px" borderColor="gray.150" bg="white" boxShadow="0 1px 3px rgba(16,24,40,.05)">
      <Flex align="center" gap="7px" minW="0">
        <Box w="9px" h="9px" borderRadius="3px" bg={t.dot} flex="none" />
        <Text fontSize="13px" fontWeight="700" color="#111827" lineHeight="1.1" noOfLines={1}>{node.title}</Text>
        <Text fontSize="8.5px" color="gray.400" noOfLines={1} ml="auto" flexShrink={0}>{node.sub}</Text>
      </Flex>
      {hasStats && (
        <Flex mt="auto" pt="7px" gap="2px" align="stretch">
          {node.connections_active != null && <StoreMetric label="active" value={fmtInt(node.connections_active)} accent="#2563eb" />}
          {node.connections != null && <StoreMetric label="open" value={fmtInt(node.connections)} />}
          {node.resident_mb != null && <StoreMetric label="memory" value={fmtMB(node.resident_mb)} />}
          {(node.reads_per_sec != null || node.writes_per_sec != null) && <StoreMetric label="r · w / s" value={`${node.reads_per_sec ?? "—"} · ${node.writes_per_sec ?? "—"}`} />}
        </Flex>
      )}
    </Flex>;
  }
  // service — CPU / RAM / Disk bars (or delivery% chip)
  const bars = [["CPU", node.cpu_pct, cpuT], ["RAM", node.ram_pct, ramT], ["Disk", node.disk_pct, ramT]].filter((b) => b[1] != null);
  const dTone = node.delivery_pct != null ? (node.delivery_pct < 85 ? "warn" : "ok") : "ok";
  // Clickable when the box exposes a per-service breakdown (servers[]) — opens the detail modal.
  const svcCount = (node.servers || []).reduce((s, b) => s + (b.services ? b.services.length : 0), 0);
  const clickable = svcCount > 0 && typeof onOpenDetail === "function";
  return (
    <Flex direction="column" h="100%" p="10px" borderRadius="lg" borderWidth="1px" borderColor={crit ? "red.200" : "gray.150"} bg={crit ? TONE.crit.bg : "white"}
      boxShadow={crit ? `0 0 0 2px ${TONE.crit.dot}22` : "0 1px 3px rgba(16,24,40,.05)"}
      cursor={clickable ? "pointer" : "default"} onClick={clickable ? () => onOpenDetail(node) : undefined}
      _hover={clickable ? { borderColor: "blue.300", boxShadow: "0 0 0 2px rgba(109,124,255,.18)" } : undefined}
      title={clickable ? "Click for the box's per-service breakdown" : undefined} transition="border-color .15s, box-shadow .15s">
      <Flex justify="space-between" align="flex-start" gap={1}>
        <Text fontSize="12.5px" fontWeight="700" color="#111827" lineHeight="1.1">{node.title}</Text>
        <Badge colorScheme={t.scheme} variant="subtle" fontSize="8px" flexShrink={0}>{pillLabel(node.status, node.id === bottleneckId)}</Badge>
      </Flex>
      <Text fontSize="8px" color="gray.400" letterSpacing="0.04em" textTransform="uppercase" mt="1px" mb="auto" noOfLines={1}>{node.sub}</Text>
      {node.connections != null && (
        <HStack spacing={1} mt="4px" alignSelf="flex-start" bg="blue.50" borderRadius="full" px={2} py="1.5px">
          <Text fontSize="13px" fontFamily="mono" fontWeight="700" color="blue.600">{fmtInt(node.connections)}</Text>
          <Text fontSize="9.5px" color="blue.500">active conns</Text>
        </HStack>
      )}
      {node.tps != null && (
        <HStack spacing={1} mt="4px" alignSelf="flex-start" bg="purple.50" borderRadius="full" px={2} py="1.5px">
          <Text fontSize="13px" fontFamily="mono" fontWeight="700" color="purple.600">{fmtTps(node.tps)}</Text>
          <Text fontSize="9.5px" color="purple.500">{node.hits_provider ? "to providers" : "throughput"}</Text>
        </HStack>
      )}
      {node.ch_parts != null && (
        <HStack spacing="6px" mt="5px" fontFamily="mono" flexWrap="wrap">
          {node.ch_disk_gb != null && <Text fontSize="11px" fontWeight="700" color="#111827">{node.ch_disk_gb}<Text as="span" fontSize="8.5px" fontWeight="500" color="gray.400"> GB</Text></Text>}
          <Text fontSize="11px" color="gray.600">{fmtInt(node.ch_parts)}<Text as="span" fontSize="8.5px" color="gray.400"> parts</Text></Text>
          {node.ch_merges != null && <Text fontSize="11px" color={node.ch_merges > 0 ? "blue.600" : "gray.500"}>{node.ch_merges}<Text as="span" fontSize="8.5px" color="gray.400"> merges</Text></Text>}
        </HStack>
      )}
      {bars.length ? (
        <VStack spacing="3px" align="stretch" mt="6px">
          {bars.map(([lbl, v, tone]) => <MiniBar key={lbl} label={lbl} v={v} tone={tone} />)}
          {node.lag_avg_s != null && <Text fontSize="8px" color="gray.400" alignSelf="flex-end" mt="1px">lag {node.lag_avg_s}s</Text>}
        </VStack>
      ) : node.status === "not_wired" ? (
        <Text fontSize="10px" color="gray.400" mt="6px">{node.note}</Text>
      ) : node.delivery_pct != null ? (
        <HStack spacing={1.5} alignSelf="flex-start" bg={TONE[dTone].bg} borderRadius="full" px={2} py="2px" mt="6px">
          <Box w="6px" h="6px" borderRadius="full" bg={TONE[dTone].dot} />
          <Text fontSize="11px" fontFamily="mono" fontWeight="700" color={TONE[dTone].fg}>{node.delivery_pct}%</Text>
          <Text fontSize="8.5px" color={TONE[dTone].fg} opacity={0.8}>delivered</Text>
        </HStack>
      ) : null}
      {clickable && <Text fontSize="7.5px" color="gray.400" mt="3px" alignSelf="flex-end">{svcCount} services ›</Text>}
    </Flex>
  );
}

// Drill-down: the box(es) behind a clicked service node — overall utilization + every PM2 service.
function fmtUptime(s) {
  if (s == null) return "—";
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
  return d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${m}m`;
}
function ServerDetailModal({ node, onClose }) {
  const servers = node?.servers || [];
  const isBuffer = node?.kind === "buffer";
  const queues = node?.queues || [];
  return (
    <Modal isOpen={!!node} onClose={onClose} size="xl" isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="16px">{node?.title}{isBuffer ? " — BullMQ queues" : " — box breakdown"}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={5}>
          {isBuffer ? (
            <Box>
              <Flex justify="space-between" align="baseline" wrap="wrap" gap={2} mb={3}>
                <Text fontSize="13px" fontWeight="700" color="#111827" fontFamily="mono">{node.sub || node.title}</Text>
                <Text fontSize="10px" color="gray.400" fontFamily="mono">
                  {node.mem_used_mb != null ? `${fmtMB(node.mem_used_mb)} used` : ""}
                  {node.reads_per_sec != null ? ` · r ${node.reads_per_sec} · w ${node.writes_per_sec}/s` : ""}
                  {` · ${queues.length} queues`}
                </Text>
              </Flex>
              {queues.length === 0 ? <Text fontSize="13px" color="gray.500">No BullMQ queues discovered on this Redis.</Text> : (
                <Box overflowX="auto">
                  <Table size="sm" variant="simple">
                    <Thead><Tr>
                      <Th fontSize="9px">Queue</Th><Th fontSize="9px" isNumeric>Waiting</Th><Th fontSize="9px" isNumeric>Active</Th>
                      <Th fontSize="9px" isNumeric>Delayed</Th><Th fontSize="9px" isNumeric>Failed</Th><Th fontSize="9px" isNumeric>Depth</Th>
                    </Tr></Thead>
                    <Tbody>
                      {queues.slice().sort((a, b) => (b.depth || 0) - (a.depth || 0)).map((q, i) => (
                        <Tr key={q.queue || i}>
                          <Td fontSize="11px" fontFamily="mono" fontWeight="600">{q.queue}</Td>
                          <Td fontSize="11px" fontFamily="mono" isNumeric color={q.waiting > 0 ? TONE.warn.fg : "gray.600"}>{fmtInt(q.waiting ?? 0)}</Td>
                          <Td fontSize="11px" fontFamily="mono" isNumeric color={q.active > 0 ? "blue.600" : "gray.600"}>{fmtInt(q.active ?? 0)}</Td>
                          <Td fontSize="11px" fontFamily="mono" isNumeric color="gray.500">{fmtInt(q.delayed ?? 0)}</Td>
                          <Td fontSize="11px" fontFamily="mono" isNumeric color={q.failed > 0 ? TONE.crit.fg : "gray.500"}>{fmtInt(q.failed ?? 0)}</Td>
                          <Td fontSize="11px" fontFamily="mono" isNumeric fontWeight="700" color={q.depth > 0 ? "#111827" : "gray.400"}>{fmtInt(q.depth ?? 0)}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              )}
            </Box>
          ) : servers.length === 0 ? (
            <Text fontSize="13px" color="gray.500">No active monitor for this stage.</Text>
          ) : servers.map((b, bi) => {
            const util = [["CPU", b.cpu_pct, cpuT], ["RAM", b.ram_pct, ramT], ["Disk", b.disk_pct, ramT]].filter((x) => x[1] != null);
            return (
              <Box key={b.node_name || bi} mb={bi < servers.length - 1 ? 5 : 0}>
                <Flex justify="space-between" align="baseline" wrap="wrap" gap={2}>
                  <Text fontSize="13px" fontWeight="700" color="#111827" fontFamily="mono">{b.node_name}</Text>
                  <Text fontSize="10px" color="gray.400">{b.cores != null ? `${b.cores} cores` : ""}{b.ram_total_mb != null ? ` · ${(b.ram_total_mb / 1024).toFixed(1)} GB RAM` : ""}{b.process_count != null ? ` · ${b.process_count} services` : ""}</Text>
                </Flex>
                <HStack spacing={4} mt={2} mb={3} wrap="wrap">
                  {util.map(([lbl, v, tone]) => (
                    <HStack key={lbl} spacing={1.5}>
                      <Box w="8px" h="8px" borderRadius="full" bg={TONE[tone(v)].dot} />
                      <Text fontSize="11px" color="gray.500">{lbl}</Text>
                      <Text fontSize="12px" fontFamily="mono" fontWeight="700" color={TONE[tone(v)].fg}>{v}%</Text>
                    </HStack>
                  ))}
                </HStack>
                <Box overflowX="auto">
                  <Table size="sm" variant="simple">
                    <Thead><Tr>
                      <Th fontSize="9px">Service</Th><Th fontSize="9px">Status</Th>
                      <Th fontSize="9px" isNumeric>CPU</Th><Th fontSize="9px" isNumeric>Mem</Th>
                      <Th fontSize="9px" isNumeric>Restarts</Th><Th fontSize="9px" isNumeric>Uptime</Th>
                    </Tr></Thead>
                    <Tbody>
                      {(b.services || []).map((s, si) => (
                        <Tr key={s.name || si}>
                          <Td fontSize="11px" fontFamily="mono" fontWeight="600">{s.name}</Td>
                          <Td><Badge colorScheme={s.status === "online" ? "green" : "red"} variant="subtle" fontSize="8px">{s.status || "—"}</Badge></Td>
                          <Td fontSize="11px" fontFamily="mono" isNumeric color={s.cpu_pct >= 60 ? TONE.warn.fg : "gray.700"}>{s.cpu_pct != null ? `${s.cpu_pct}%` : "—"}</Td>
                          <Td fontSize="11px" fontFamily="mono" isNumeric>{s.mem_mb != null ? `${s.mem_mb} MB` : "—"}</Td>
                          <Td fontSize="11px" fontFamily="mono" isNumeric color={s.restarts > 5 ? TONE.warn.fg : "gray.600"}>{s.restarts ?? "—"}</Td>
                          <Td fontSize="11px" fontFamily="mono" isNumeric color="gray.600">{fmtUptime(s.uptime_s)}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
                {node?.note && bi === 0 && <Text fontSize="10px" color="gray.400" mt={2}>{node.note}</Text>}
                {bi < servers.length - 1 && <Divider mt={4} />}
              </Box>
            );
          })}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

function EdgeLabel({ edge }) {
  const tps = edge.f?.tps, cong = edge.f?.congested;
  const transform = edge.lpos === "right" ? "translate(6px,-50%)" : edge.lpos === "below" ? "translate(-50%,2px)" : "translate(-50%,-100%)";
  const rateless = tps == null;
  return (
    <Box position="absolute" left={`${edge.lx}px`} top={`${edge.ly}px`} transform={transform} pointerEvents="none">
      <Box bg="rgba(255,255,255,0.94)" borderRadius="5px" px="5px" py="1px" textAlign="center" whiteSpace="nowrap" boxShadow="0 0 0 3px rgba(255,255,255,0.94)">
        {rateless ? (
          <Text fontSize="8.5px" fontWeight="600" letterSpacing="0.03em" color="gray.400" lineHeight="1.2">{edge.f?.label || "lookup"}</Text>
        ) : (
          <>
            <Text fontSize="11px" fontFamily="mono" fontWeight="700" lineHeight="1.15" color={cong ? TONE.crit.fg : tps > 0 ? "blue.600" : "gray.400"}>{fmtTps(tps)}</Text>
            {edge.f?.label && <Text fontSize="8px" color="gray.400" lineHeight="1.1">{edge.f.label}{edge.f.est ? " ~est" : ""}</Text>}
          </>
        )}
      </Box>
    </Box>
  );
}

/**
 * The responsive flow canvas. Measures its width, scales the layout horizontally to fill it, then
 * renders animated edges + node cards + band labels. `bandLabels` = [{ text, x, y }] (x in base units).
 */
export default function FlowDiagram({ layout, buildEdges, pipeline, baseW, height, title, caption, bandLabels = [] }) {
  const flows = pipeline?.flows || [];
  const byId = useMemo(() => Object.fromEntries((pipeline?.nodes || []).map((n) => [n.id, n])), [pipeline]);
  const flow = (a, b) => flows.find((f) => f.from === a && f.to === b);
  const bn = pipeline?.bottleneck;
  const bottleneckId = bn && !bn.healthy ? bn.stage : null;

  const wrapRef = useRef(null);
  const [cw, setCw] = useState(baseW);
  const [detailNode, setDetailNode] = useState(null); // clicked service → box breakdown modal
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const measure = () => setCw(Math.max(760, el.clientWidth));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const sx = cw / baseW;
  const SL = useMemo(() => Object.fromEntries(Object.entries(layout).map(([k, v]) => [k, { ...v, x: v.x * sx, w: v.w * sx }])), [sx, layout]);
  const edges = useMemo(() => buildEdges(SL, flow), [SL, flows]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="xl" p={5}>
      {(title || caption) && (
        <Flex justify="space-between" align="baseline" mb={3}>
          {title && <Text fontSize="11px" color="gray.500" fontWeight="700" letterSpacing="0.12em" textTransform="uppercase">{title}</Text>}
          {caption && <Text fontSize="10px" color="gray.400">{caption}</Text>}
        </Flex>
      )}
      <Box ref={wrapRef} pb={2}>
        <Box position="relative" w={`${cw}px`} h={`${height}px`}>
          <Box as="svg" viewBox={`0 0 ${cw} ${height}`} position="absolute" inset={0} w="100%" h="100%" style={{ overflow: "visible" }}>
            {edges.map((e) => {
              const tps = e.f?.tps, cong = e.f?.congested;
              const hasFlow = tps > 0; // no throughput → static line, no particles
              const stroke = cong ? TONE.crit.dot : e.dashed ? "#cbd2dc" : e.ret ? "#c7d9c9" : "#dde1e9";
              const color = cong ? TONE.crit.dot : e.ret ? TONE.ok.dot : FLOW, d = dur(tps);
              return (
                <g key={e.id}>
                  <path id={e.id} d={e.d} fill="none" stroke={stroke} strokeWidth={cong ? 3 : 2.2} strokeDasharray={e.ret ? "5 5" : e.dashed ? "2 4" : "0"} strokeLinecap="round" strokeLinejoin="round" />
                  {hasFlow && [0, 1, 2].map((i) => {
                    const pd = e.dashed ? 7.5 : d;
                    return (
                      <circle key={i} r={e.dashed ? 2.6 : cong ? 4.2 : 3.6} fill={e.dashed ? "#aeb6c4" : color} opacity={e.dashed ? 0.85 : 0.95}>
                        <animateMotion dur={`${pd}s`} begin={`${(i * pd) / 3}s`} repeatCount="indefinite"><mpath href={`#${e.id}`} /></animateMotion>
                      </circle>
                    );
                  })}
                </g>
              );
            })}
          </Box>
          {edges.map((e) => <EdgeLabel key={e.id} edge={e} />)}
          {Object.keys(layout).map((id) => {
            // Never hide a box: if the backend sent no node for this slot, render it DISABLED
            // ("no active monitor") rather than dropping it, so the operator sees the gap.
            const node = byId[id] || { id, kind: layout[id].kind || "service", status: "not_wired", note: "no active monitor",
              title: id.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()), sub: "" };
            return (
              <Box key={id} position="absolute" left={`${SL[id].x}px`} top={`${SL[id].y}px`} w={`${SL[id].w}px`} h={`${SL[id].h}px`}><NodeCard node={node} bottleneckId={bottleneckId} onOpenDetail={setDetailNode} /></Box>
            );
          })}
          {bandLabels.map((bl) => (
            <Text key={bl.text} position="absolute" left={`${bl.x * sx}px`} top={`${bl.y}px`} fontSize="9px" fontWeight="700" letterSpacing="0.1em" color="gray.400" textTransform="uppercase" pointerEvents="none">{bl.text}</Text>
          ))}
        </Box>
      </Box>
      <ServerDetailModal node={detailNode} onClose={() => setDetailNode(null)} />
    </Box>
  );
}
